import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  // Divider,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { signUpSchema } from "constants/formValidation";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoginTitle, RootBox } from "./mui.styles";

import { yupResolver } from "@hookform/resolvers/yup";
import AuthPagesDescription from "components/AuthPagesDescription";
import CustomCheckBox from "components/general/CustomCheckBox";
import OTPVerification from "components/general/auth/OTPVerification";
import SignInOptions from "components/general/auth/SignInOptions";
import { CustomTextField } from "components/mui-common/CustomTextField";
import { APP_DOCS, OTP_TYPE, REGISTRATION_TYPES } from "constants/general";
import { ROUTES } from "constants/routes";
import { useNavigate } from "react-router-dom";
import {
  setIsSubmitting,
  setOtpTokenData,
  setOtpVerification,
  setSignUpFormError,
  signUpUserAction,
} from "slices/authReducers";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { API_URL } from "utils/axios.util";
import { getUserProfile } from "slices/userReducer";

/**
 *  @returns: Signup form component
 */
const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(signUpSchema),
  });

  const { authReducer } = useAppSelector((state) => state);
  const { isSubmitting, signUpFormError, otpVerification } = authReducer;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setIsSubmitting(false));
    dispatch(setSignUpFormError(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setOtpVerification(false));
      dispatch(setOtpTokenData({}));
    };
  }, [dispatch]);

  console.log({ errors });

  /**
   * @method: Method to submit the sign up form
   */
  const doSignUp = async (data: any) => {
    console.log("values submitted");
    data.registration_type = REGISTRATION_TYPES.EMAIL;
    dispatch(
      signUpUserAction({
        data,
        navigate: () => goToLogin(),
      })
    );
  };

  /**
   * @method: Method to redirect user to Login page
   */
  const goToLogin = async () => {
    navigate(ROUTES.HOME);
    !isSubmitting && dispatch(getUserProfile());
  };

  return (
    <Box
      sx={{
        backgroundColor: isMobileView ? "transparent" : "primary.main",
        minHeight: isMobileView ? "100%" : "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={8}
        sx={{ width: "100%", height: "100vh" }}
      >
        {!isMobileView && (
          <Box sx={{ width: 450 }}>
            <AuthPagesDescription />
          </Box>
        )}
        <RootBox
          sx={{
            maxWidth: isMobileView ? "100%" : "384px",
            ...(isMobileView && { width: "100%", height: "100%" }),
          }}
        >
          {otpVerification ? (
            <OTPVerification
              onSuccess={() => {
                navigate(ROUTES.HOME);
                !isSubmitting && dispatch(getUserProfile());
              }}
              email={getValues().email}
              otpType={OTP_TYPE.EMAIL}
            />
          ) : (
            <Fragment>
              <Box p={3}>
                <LoginTitle>Sign up now to join Haystackers!</LoginTitle>
                <form
                  style={{
                    marginTop: Object.keys(errors)?.length ? "15px" : "50px",
                  }}
                  onSubmit={handleSubmit(doSignUp)}
                >
                  <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    spacing={
                      isMobileView ? 3.5 : Object.keys(errors)?.length ? 1 : 2.5
                    }
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({
                        field: { onChange },
                        fieldState: { error },
                      }) => (
                        <CustomTextField
                          variant="standard"
                          placeholder="Name"
                          fullWidth
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message ?? ""}
                          value={getValues()?.name}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="email"
                      // rules={{ ...FORM_VALIDATIONS.REQUIRED_EMAIL }}
                      render={({
                        field: { onChange },
                        fieldState: { error },
                      }) => (
                        <CustomTextField
                          variant="standard"
                          placeholder="Email"
                          fullWidth
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message ?? ""}
                          value={getValues()?.email}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="password"
                      render={({
                        field: { onChange },
                        fieldState: { error },
                      }) => (
                        <CustomTextField
                          variant="standard"
                          placeholder="Password"
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          value={getValues()?.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? (
                                    <VisibilityOff htmlColor="#9C9C9C" />
                                  ) : (
                                    <Visibility htmlColor="#9C9C9C" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message ?? ""}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="confirmPassword"
                      render={({
                        field: { onChange },
                        fieldState: { error },
                      }) => (
                        <CustomTextField
                          variant="standard"
                          value={getValues()?.confirmPassword}
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff htmlColor="#9C9C9C" />
                                  ) : (
                                    <Visibility htmlColor="#9C9C9C" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          onChange={onChange}
                          error={!!error}
                          helperText={error?.message ?? ""}
                        />
                      )}
                    />
                  </Stack>

                  <Controller
                    control={control}
                    name="tandc"
                    // rules={{
                    //   required: {
                    //     value: true,
                    //     message: 'Please read & accept the terms and policies.',
                    //   },
                    // }}
                    defaultValue={false}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <Box mt={1.5} mb={2}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box width={80}>
                            <CustomCheckBox
                              id="tandc"
                              onChange={onChange}
                              value={value}
                              label={<></>}
                            />
                          </Box>

                          <Typography
                            color="#565656"
                            fontSize={12}
                            fontWeight={400}
                          >
                            By signing up, you are confirming you are at least
                            25 years old and agree to the{" "}
                            <Typography
                              component="span"
                              color="#307AFF"
                              fontSize={12}
                              mt={1}
                              fontWeight={600}
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                window.open(`${API_URL}/public/${APP_DOCS.PP}`)
                              }
                            >
                              Terms and Policies of Haystackers Holdings
                              Corporation.
                            </Typography>
                          </Typography>
                        </Box>

                        {!!error && (
                          <Typography color="error" fontSize={12} mt={1}>
                            {error?.message ?? ""}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                  {signUpFormError && (
                    <Alert style={{ marginBottom: "1rem" }} severity="error">
                      {signUpFormError}
                    </Alert>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    fullWidth
                    sx={{ fontWeight: 400, fontSize: 18 }}
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    endIcon={isSubmitting && <CircularProgress size={15} />}
                  >
                    Signup
                  </Button>
                </form>
                <Typography
                  sx={{ textAlign: "center" }}
                  color="#565656"
                  fontSize={14}
                  mt={2}
                  fontWeight={500}
                >
                  Already have an account?{" "}
                  <Typography
                    component="span"
                    color="primary"
                    fontSize={14}
                    mt={1}
                    fontWeight={500}
                    onClick={() => navigate(ROUTES.SIGNIN)}
                    sx={{ cursor: "pointer" }}
                  >
                    Log In Now
                  </Typography>
                </Typography>
              </Box>
              <Divider sx={{ mt: 2, width: "100%", borderBottomWidth: 6 }} />
              <Box
                p={3}
                display="flex"
                justifyContent="space-between"
                flexDirection="row"
                alignItems="center"
              >
                <Typography color="#565656" fontWeight={400} fontSize={13}>
                  Or Continue with
                </Typography>
                <Box>
                  <SignInOptions />
                </Box>
              </Box>
            </Fragment>
          )}
          {/* <Divider orientation="horizontal" />
      <Button
        onClick={() => navigate(ROUTES.SIGNIN)}
        color="primary"
        variant="contained"
        fullWidth
        sx={{ fontWeight: 400, fontSize: 18, marginTop: '1.5rem' }}
      >
        Log in
      </Button> */}
        </RootBox>
      </Stack>
    </Box>
  );
};

export default SignupForm;
